import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import i18next from 'i18next';

import Link from 'components/Link';

import {
  content as contentPropType,
  liveBlogCard as liveBlogCardPropType,
} from 'lib/CustomPropTypes';
import { querySettings } from 'lib/liveBlogPkgHelpers';
import getLink from 'lib/getLiveBlogEmbeddedLink';

import Card from '../Card';

const { queryLimit: maxCards } = querySettings;

function CardList(props) {
  const {
    isEmbedded,
    cards,
    content,
    id,
    defaultBreakingStyle,
    defaultLabel,
    isRailAdjacent,
  } = props;

  const {
    computedValues,
    metadata: {
      label,
      showTimestamp: showTimestampSettings,
      showBreakingStyle,
      showDek,
    } = {},
  } = content || {};
  const { headline, dek, url } = computedValues || {};

  const showCards = cards && !!cards.length;
  const showTimestamp = showTimestampSettings !== false;
  const showBreaking = defaultBreakingStyle
    // if default is to be breaking, value must explicitly be set to false to be turned off
    ? showBreakingStyle !== false
    // if default is to be not breaking, value must explicitly be set to true to be turned on
    : showBreakingStyle === true;

  const labelToDisplay = label || (defaultLabel ? i18next.t(defaultLabel) : null);

  const { href, deviceUrl } = getLink(url, id, isEmbedded);

  return (
    <div
      className={classNames(
        'live-blog-pkg',
        {
          'live-blog-pkg--embedded': isEmbedded,
          'live-blog-pkg--breaking': showBreaking,
        },
      )}
      data-contentid={id}
      data-test="live-blog-pkg"
    >
      { headline && (
        // Must send href as null since Link component uses deviceUrl as separate fallback prop
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <Link
          className="live-blog-pkg__column"
          data-test="live-blog-pkg__link"
          href={deviceUrl ? null : href} // use deviceUrl if embedded
          deviceUrl={deviceUrl}
        >
          <h2 className={classNames('live-blog-pkg__headline', { 'live-blog-pkg__headline--rail': isRailAdjacent })} data-test="live-blog-pkg__headline">
            <span
              className={classNames(
                'live-blog-pkg__headline__label',
                {
                  'live-blog-pkg__headline__label--breaking': showBreaking,
                  dn: !labelToDisplay,
                },
              )}
              data-test="live-blog-pkg__label"
            >
              {labelToDisplay}
              &nbsp;
              {'/ '}
            </span>
            <span className="live-blog-pkg__headline__text">{headline}</span>
          </h2>
        </Link>
      )}

      { dek && (showDek || (cards && cards.length <= 2)) && (
        <p className="live-blog-pkg__dek live-blog-pkg__column" data-test="live-blog-pkg__dek">
          {dek}
        </p>
      )}

      { showCards && (
        <ul
          data-test="live-blog-pkg__list"
          className={classNames('live-blog-pkg__list live-blog-pkg__column', {
            'live-blog-pkg__list--wrapped': !showDek,
            'live-blog-pkg__list--rail': isRailAdjacent,
          })}
        >
          {cards.map((card, index) => {
            if (index < maxCards) {
              return (
                <Card
                  isBreaking={showBreaking}
                  isEmbedded={isEmbedded}
                  showTimestamp={showTimestamp}
                  content={card}
                  key={`lb-${card.id}`}
                />
              );
            }
            return null;
          })}
        </ul>
      )}
    </div>
  );
}

CardList.propTypes = {
  isEmbedded: PropTypes.bool,
  cards: PropTypes.arrayOf(PropTypes.shape(liveBlogCardPropType)).isRequired,
  content: contentPropType.isRequired,
  id: PropTypes.string.isRequired,
  defaultBreakingStyle: PropTypes.bool,
  isRailAdjacent: PropTypes.bool,
  defaultLabel: PropTypes.string,
};

CardList.defaultProps = {
  isEmbedded: false,
  isRailAdjacent: false,
  defaultBreakingStyle: false,
  defaultLabel: '',
};

export default CardList;
